import { MarketAnalysis, VisualizationRecommendation } from './marketplace-schemas';

export const marketAnalysis: MarketAnalysis = {
  total_market_size: {
    amount: 400,
    year: 2024,
    growth_rate: 13.32,
    source: 'Fortune Business Insights - Hyperlocal Services Market Report'
  },
  segment_analysis: [
    {
      segment: 'home_improvement',
      market_share: 45,
      growth_rate: 12.5,
      leading_companies: ['angi', 'thumbtack', 'porch_group']
    },
    {
      segment: 'cleaning_services',
      market_share: 25,
      growth_rate: 15.2,
      leading_companies: ['handy', 'thumbtack', 'taskrabbit']
    },
    {
      segment: 'handyman_services',
      market_share: 20,
      growth_rate: 18.7,
      leading_companies: ['taskrabbit', 'thumbtack', 'angi']
    },
    {
      segment: 'moving_services',
      market_share: 5,
      growth_rate: 8.3,
      leading_companies: ['taskrabbit', 'handy']
    },
    {
      segment: 'professional_services',
      market_share: 5,
      growth_rate: 22.1,
      leading_companies: ['thumbtack', 'angi']
    }
  ],
  trends: [
    {
      trend_name: 'AI-Driven Service Matching',
      description: 'Implementation of AI and machine learning for improved professional-customer matching, pricing optimization, and demand forecasting. Companies using AI report 3.7x ROI improvements.',
      impact_level: 'high',
      timeline: '2024-2026',
      affected_companies: ['thumbtack', 'angi', 'porch_group']
    },
    {
      trend_name: 'Mobile-First Platform Evolution',
      description: 'Shift toward mobile-native experiences with instant booking, real-time tracking, and seamless payment integration. Mobile usage drives 70%+ of platform interactions.',
      impact_level: 'high',
      timeline: '2024-2025',
      affected_companies: ['taskrabbit', 'thumbtack', 'handy']
    },
    {
      trend_name: 'Professional Retention Focus',
      description: 'Increased emphasis on service provider retention through better tools, higher payouts, and professional development. Angi improved pro retention by 700 basis points in 2024.',
      impact_level: 'high',
      timeline: '2024-2026',
      affected_companies: ['angi', 'thumbtack', 'taskrabbit']
    },
    {
      trend_name: 'Integrated Insurance and Warranties',
      description: 'Expansion of platform-provided insurance, warranties, and risk management services to differentiate and increase customer confidence.',
      impact_level: 'medium',
      timeline: '2024-2027',
      affected_companies: ['porch_group', 'angi']
    },
    {
      trend_name: 'Hyperlocal Service Expansion',
      description: 'Extension beyond core services to include beauty, wellness, automotive, and specialized professional services. Market expected to grow at 13.32% CAGR.',
      impact_level: 'medium',
      timeline: '2024-2028',
      affected_companies: ['thumbtack', 'taskrabbit']
    },
    {
      trend_name: 'Platform Consolidation',
      description: 'Merger and acquisition activity as companies seek scale advantages and market consolidation. ANGI preparing for spin-off, continued M&A expected.',
      impact_level: 'medium',
      timeline: '2024-2026',
      affected_companies: ['angi', 'porch_group']
    }
  ],
  competitive_landscape: [
    {
      tier: 'tier_1',
      companies: ['thumbtack', 'angi'],
      characteristics: [
        'Market leadership with $3B+ valuations',
        'Comprehensive service offerings (1000+ services)',
        'Advanced AI/ML implementations',
        'Large professional networks (200,000+ providers)',
        'Strong brand recognition and marketing spend'
      ]
    },
    {
      tier: 'tier_2',
      companies: ['taskrabbit', 'porch_group'],
      characteristics: [
        'Specialized focus areas with strong execution',
        'Unique value propositions (IKEA integration, insurance)',
        'Moderate professional networks (50,000-100,000 providers)',
        'International presence or specialized verticals',
        'Technology differentiation in specific areas'
      ]
    },
    {
      tier: 'emerging',
      companies: ['handy'],
      characteristics: [
        'Acquired platforms integrating into larger ecosystems',
        'Fixed-price models and operational efficiency focus',
        'Regional or service-specific market strength',
        'Technology modernization in progress',
        'Brand repositioning within parent company'
      ]
    }
  ],
  analysis_date: '2024-08-22',
  methodology: 'Comprehensive analysis combining public financial data, industry reports, company communications, and technology assessment across five key marketplace players',
  confidence_level: 'high'
};

export const visualizationRecommendations: VisualizationRecommendation[] = [
  {
    chart_type: 'Market Share Bubble Chart',
    data_fields: ['revenue', 'valuation', 'gmv', 'active_users'],
    title: 'US Local Service Marketplace Competitive Positioning 2024',
    description: 'Shows relative market position by revenue (x-axis), valuation (y-axis), with bubble size representing GMV and color coding for user base size',
    priority: 'high',
    interactive_features: ['hover details', 'company filtering', 'metric switching']
  },
  {
    chart_type: 'Revenue Growth Timeline',
    data_fields: ['revenue', 'year', 'company_name'],
    title: 'Revenue Growth Trajectories 2020-2025',
    description: 'Multi-line chart showing revenue evolution and projected growth for each major platform',
    priority: 'high',
    interactive_features: ['zoom', 'data point details', 'company toggle']
  },
  {
    chart_type: 'Service Category Heatmap',
    data_fields: ['primary_segments', 'market_share', 'growth_rate'],
    title: 'Market Share by Service Category',
    description: 'Heatmap showing which companies dominate which service categories and growth potential',
    priority: 'high',
    interactive_features: ['segment filtering', 'metric switching', 'drill-down details']
  },
  {
    chart_type: 'Technology Differentiation Radar',
    data_fields: ['ai_integration', 'mobile_first', 'instant_booking', 'real_time_tracking', 'automation_level'],
    title: 'Technology Capabilities Comparison',
    description: 'Radar chart comparing technology capabilities across platforms to identify competitive advantages',
    priority: 'medium',
    interactive_features: ['company overlay', 'capability details', 'benchmark comparison']
  },
  {
    chart_type: 'Geographic Coverage Map',
    data_fields: ['primary_markets', 'total_cities', 'coverage_density'],
    title: 'Geographic Market Penetration',
    description: 'Interactive map showing market coverage density and expansion opportunities',
    priority: 'medium',
    interactive_features: ['zoom', 'layer toggle', 'market details', 'competitive overlap']
  },
  {
    chart_type: 'Business Model ROI Analysis',
    data_fields: ['business_model', 'take_rate', 'cac', 'revenue'],
    title: 'Business Model Effectiveness Comparison',
    description: 'Comparative analysis of different business models and their financial performance metrics',
    priority: 'medium',
    interactive_features: ['model filtering', 'metric correlation', 'trend analysis']
  },
  {
    chart_type: 'Funding and Valuation Timeline',
    data_fields: ['funding_rounds', 'valuation', 'acquisition_details'],
    title: 'Investment and M&A Activity 2020-2024',
    description: 'Timeline visualization of funding rounds, valuations, and acquisition activity in the sector',
    priority: 'low',
    interactive_features: ['timeline scrubbing', 'event details', 'investor filtering']
  }
];

// Key insights and recommendations
export const keyInsights = {
  market_leaders: {
    revenue: 'Angi leads in Q4 2024 revenue ($267.9M) followed by TaskRabbit ($343.4M annually)',
    valuation: 'Thumbtack holds highest valuation at $3.2B, followed by Angi at $1.3B market cap',
    scale: 'Angi has largest professional network (250,000+ providers), Thumbtack second (200,000+)'
  },
  competitive_differentiation: {
    technology: 'Thumbtack and Angi lead in AI integration for competitive advantage',
    business_model: 'Diverse approaches: lead-generation (Thumbtack), commission (TaskRabbit), hybrid (Angi, Porch)',
    geographic: 'TaskRabbit only major player with significant international presence'
  },
  growth_opportunities: {
    ai_automation: 'Companies with AI see 3.7x ROI, significant automation potential in service matching',
    professional_retention: 'Major focus area - Angi improved retention by 700 basis points',
    service_expansion: 'Hyperlocal services beyond traditional categories show 13.32% CAGR growth'
  },
  market_risks: {
    consolidation: 'Ongoing M&A activity may reshape competitive landscape',
    cac_pressure: 'High customer acquisition costs across all platforms',
    professional_supply: 'Professional retention and quality control challenges at scale'
  },
  investment_themes: {
    ai_enabled_platforms: 'Clear ROI advantage for AI-integrated marketplaces',
    vertical_integration: 'Porch\'s insurance model shows alternative revenue diversification',
    mobile_native: 'Mobile-first experiences becoming table stakes for competition'
  }
};

export const dataQualityAssessment = {
  overall_score: 85,
  completeness: 78,
  reliability_distribution: {
    verified: 45,
    reported: 35,
    estimated: 15,
    projected: 5
  },
  source_diversity: 12,
  last_updated: '2024-08-22',
  gaps_identified: [
    'Limited 2024 take rate data for most companies',
    'Customer acquisition costs not publicly disclosed',
    'Service provider retention metrics inconsistently reported',
    'Geographic coverage details vary in specificity'
  ],
  recommendations: [
    'Supplement with industry survey data for missing metrics',
    'Monitor quarterly earnings for updated financial data',
    'Track technology adoption through company announcements',
    'Validate estimates through third-party research firms'
  ]
};